import type { CSSProperties, ReactNode } from 'react';
import { cn } from '@/lib/cn';
import type { Tone } from './Badge';

/* ── Page scaffolding ─────────────────────────────────────────────────────────
   Every screen uses Page + PageHeader. The header is sticky and hairline-ruled;
   the body is the only thing that scrolls. This keeps the console feeling like
   fixed instrument panels rather than a document.                            */

export function Page({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn('flex h-full min-h-0 flex-col', className)}>{children}</div>;
}

export function PageHeader({
  title,
  /** Small uppercase label above the title — the section the page belongs to. */
  eyebrow,
  subtitle,
  actions,
  tabs,
  className,
}: {
  title: ReactNode;
  eyebrow?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  /** Optional secondary nav row rendered flush with the bottom hairline. */
  tabs?: ReactNode;
  className?: string;
}) {
  return (
    <header
      className={cn(
        'border-line bg-canvas/85 shrink-0 border-b px-4 pt-3 backdrop-blur-sm',
        tabs ? 'pb-0' : 'pb-3',
        className
      )}
    >
      <div className="flex min-h-8 items-start gap-4">
        <div className="min-w-0 flex-1">
          {eyebrow ? (
            <div className="text-2xs text-ink-faint mb-0.5 font-medium tracking-wider uppercase">
              {eyebrow}
            </div>
          ) : null}
          <h1 className="text-ink truncate text-xl font-semibold tracking-tight">
            {title}
          </h1>
          {subtitle ? (
            <div className="text-ink-muted mt-0.5 truncate text-xs">{subtitle}</div>
          ) : null}
        </div>
        {actions ? (
          <div className="flex shrink-0 items-center gap-2 pt-0.5">{actions}</div>
        ) : null}
      </div>
      {tabs ? <div className="mt-3 -mb-px flex items-center gap-1">{tabs}</div> : null}
    </header>
  );
}

/** The scrolling region of a page. Everything after PageHeader goes in here. */
export function PageBody({
  children,
  className,
  /** Remove default padding for full-bleed content like a data grid. */
  bleed = false,
}: {
  children: ReactNode;
  className?: string;
  bleed?: boolean;
}) {
  return (
    <div
      className={cn(
        'scrollpane min-h-0 flex-1',
        // This is the scroll container, so a stacked child must keep its
        // natural height and let the pane scroll. Without this, screens that
        // make the body a flex column squash their panels instead — and
        // `Panel` is `overflow-hidden`, so the excess is silently clipped
        // (it ate the bottom two thirds of the Reports charts). Inert for the
        // non-flex bodies, which are the majority.
        '[&>*]:shrink-0',
        !bleed && 'p-4',
        className
      )}
    >
      {children}
    </div>
  );
}

/** A sticky toolbar directly under the header: filters, search, bulk actions. */
export function Toolbar({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'border-line bg-canvas flex shrink-0 flex-wrap items-center gap-2 border-b px-4 py-2',
        className
      )}
    >
      {children}
    </div>
  );
}

/* ── Panels ───────────────────────────────────────────────────────────────── */

export function Panel({
  title,
  description,
  actions,
  footer,
  children,
  className,
  bodyClassName,
  /** Drop the internal padding — for panels wrapping a table. */
  bleed = false,
  /**
   * Let a dropdown inside the panel (the product picker's list) hang past the
   * panel's edge instead of being cut off at it. The header and footer keep the
   * panel's rounded corners themselves.
   */
  allowOverflow = false,
}: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
  bleed?: boolean;
  allowOverflow?: boolean;
}) {
  return (
    <section
      className={cn(
        'border-line bg-surface flex min-w-0 flex-col rounded border',
        allowOverflow ? 'overflow-visible' : 'overflow-hidden',
        className
      )}
    >
      {title || actions ? (
        <div
          className={cn(
            'border-line flex shrink-0 items-center gap-3 border-b px-3 py-2',
            allowOverflow && 'rounded-t-[3px]'
          )}
        >
          <div className="min-w-0 flex-1">
            <h2 className="text-ink-muted truncate text-xs font-semibold tracking-wider uppercase">
              {title}
            </h2>
            {description ? (
              <p className="text-2xs text-ink-faint mt-0.5 truncate">{description}</p>
            ) : null}
          </div>
          {actions ? (
            <div className="flex shrink-0 items-center gap-1">{actions}</div>
          ) : null}
        </div>
      ) : null}
      <div className={cn('min-w-0 flex-1', !bleed && 'p-3', bodyClassName)}>
        {children}
      </div>
      {footer ? (
        <div
          className={cn(
            'border-line bg-surface-sunken text-ink-muted shrink-0 border-t px-3 py-2 text-xs',
            allowOverflow && 'rounded-b-[3px]'
          )}
        >
          {footer}
        </div>
      ) : null}
    </section>
  );
}

/** Definition list used across every detail view. Labels are quiet, values loud. */
export function DetailList({
  children,
  className,
  columns = 1,
}: {
  children: ReactNode;
  className?: string;
  columns?: 1 | 2 | 3;
}) {
  return (
    <dl
      className={cn(
        'grid gap-x-6',
        columns === 1 && 'grid-cols-1',
        columns === 2 && 'grid-cols-1 sm:grid-cols-2',
        columns === 3 && 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
        className
      )}
    >
      {children}
    </dl>
  );
}

export function Detail({
  label,
  children,
  mono = false,
  className,
}: {
  label: ReactNode;
  children: ReactNode;
  mono?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn('border-line-soft flex flex-col gap-0.5 border-b py-2', className)}
    >
      <dt className="text-2xs text-ink-faint tracking-wider uppercase">{label}</dt>
      <dd className={cn('text-ink truncate text-sm', mono && 'font-mono tabular-nums')}>
        {children}
      </dd>
    </div>
  );
}

/* ── KPI tiles ────────────────────────────────────────────────────────────────
   The dashboard vocabulary. Big mono figure, quiet label, optional delta. The
   delta colour is semantic: green up is only green when up is good, which the
   caller decides via `goodDirection`.                                        */

export function Stat({
  label,
  value,
  sub,
  delta,
  goodDirection = 'up',
  tone,
  icon,
  onClick,
  className,
}: {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  /** Percentage change vs the comparison period. */
  delta?: number | null;
  goodDirection?: 'up' | 'down' | 'none';
  tone?: Tone;
  icon?: ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  const deltaTone =
    delta === null || delta === undefined || delta === 0 || goodDirection === 'none'
      ? 'text-ink-faint'
      : delta > 0 === (goodDirection === 'up')
        ? 'text-positive'
        : 'text-negative';

  const Wrapper = onClick ? 'button' : 'div';
  return (
    <Wrapper
      onClick={onClick}
      className={cn(
        'border-line bg-surface flex min-w-0 flex-col gap-1 rounded border p-3 text-left',
        onClick && 'hover:border-line-strong hover:bg-surface-hover transition-colors',
        className
      )}
    >
      <div className="text-2xs text-ink-faint flex items-center gap-1.5 font-medium tracking-wider uppercase">
        {icon ? <span className="[&_svg]:size-3">{icon}</span> : null}
        <span className="truncate">{label}</span>
      </div>
      <div
        className={cn(
          'truncate font-mono text-2xl font-medium tracking-tight tabular-nums',
          tone === 'negative' && 'text-negative',
          tone === 'positive' && 'text-positive',
          tone === 'warn' && 'text-warn',
          tone === 'signal' && 'text-signal-ink'
        )}
      >
        {value}
      </div>
      <div className="text-2xs flex items-baseline gap-2">
        {delta !== null && delta !== undefined ? (
          <span className={cn('font-mono tabular-nums', deltaTone)}>
            {delta > 0 ? '▲' : delta < 0 ? '▼' : '—'} {Math.abs(delta).toFixed(1)}%
          </span>
        ) : null}
        {sub ? <span className="text-ink-faint truncate">{sub}</span> : null}
      </div>
    </Wrapper>
  );
}

export function StatRow({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'stagger grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-4',
        className
      )}
    >
      {children}
    </div>
  );
}

/* ── Empty / error / loading ──────────────────────────────────────────────── */

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
  compact = false,
}: {
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
  compact?: boolean;
}) {
  return (
    <div
      className={cn(
        'dot-grid flex flex-col items-center justify-center gap-2 rounded text-center',
        compact ? 'p-6' : 'p-14',
        className
      )}
    >
      {icon ? (
        <div className="border-line bg-surface text-ink-faint mb-1 flex size-9 items-center justify-center rounded border [&_svg]:size-4">
          {icon}
        </div>
      ) : null}
      <div className="text-ink text-sm font-medium">{title}</div>
      {description ? (
        <div className="text-ink-faint max-w-sm text-xs leading-relaxed">
          {description}
        </div>
      ) : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

export function Skeleton({
  className,
  style,
}: {
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div
      className={cn('bg-surface-hover animate-pulse rounded-xs', className)}
      style={style}
      aria-hidden
    />
  );
}

/** Standard loading placeholder for a table body. */
export function TableSkeleton({ rows = 8, cols = 5 }: { rows?: number; cols?: number }) {
  return (
    <div className="divide-line-soft divide-y">
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} className="flex h-7 items-center gap-4 px-3">
          {Array.from({ length: cols }, (_, c) => (
            <Skeleton
              key={c}
              className="h-2.5"
              // Varying widths read as data far better than uniform bars.
              style={{ width: `${[28, 14, 10, 18, 12, 9][c % 6]}%` }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Inline failure block. Used by route error boundaries and failed panels. */
export function ErrorState({
  title = 'Could not load this',
  message,
  onRetry,
  className,
}: {
  title?: ReactNode;
  message?: ReactNode;
  onRetry?: () => void;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'border-negative/30 bg-negative-muted flex flex-col items-center justify-center gap-2 rounded border p-8 text-center',
        className
      )}
    >
      <div className="text-ink text-sm font-medium">{title}</div>
      {message ? (
        <div className="text-2xs text-ink-muted max-w-md font-mono leading-relaxed">
          {message}
        </div>
      ) : null}
      {onRetry ? (
        <button
          onClick={onRetry}
          className="border-line bg-surface hover:bg-surface-hover mt-1 rounded border px-2 py-1 text-xs"
        >
          Try again
        </button>
      ) : null}
    </div>
  );
}

/** A left colour bar signalling urgency on list rows (expiring, low stock). */
export function UrgencyBar({ tone }: { tone: Tone }) {
  const map: Record<Tone, string> = {
    neutral: 'bg-line',
    signal: 'bg-signal',
    positive: 'bg-positive',
    negative: 'bg-negative',
    warn: 'bg-warn',
    info: 'bg-info',
  };
  return (
    <span className={cn('absolute inset-y-0 left-0 w-0.5', map[tone])} aria-hidden />
  );
}
